import React, { useCallback, useEffect, useState } from 'react'
import {
	Box, Button, Chip, Collapse, IconButton,
	LinearProgress, Table, TableBody, TableCell, TableHead, TableRow,
	Tooltip, Typography,
} from '@mui/material'
import { dsColorNew } from '@palette'
import { AppCardTitle, AppNote, AppTableSkeleton, secondaryButtonSx, AppInfoButton, AppEmptyLine, AppBulkEditAction } from '@components'
import { Link as RouterLink } from 'react-router-dom'
import { OpenInNew as OpenIcon, RefreshOutlined as RefreshIcon, ExpandMore as ExpandIcon, ExpandLess as CollapseIcon } from 'Pages/DsRepricing/@components/icons'
import { DS_REPRICING_ROUTES } from '@constants/routes'
import ChangesDialog, { OUTCOME_STYLE } from 'Pages/DsRepricing/Schedule/@components/ChangesDialog'
import { scheduleApi } from 'Pages/DsRepricing/Schedule/@services/api'
import useToast from '@hooks/useToast'
import { displayDay } from '@utils'
import { format } from 'date-fns'
import { toneChipSx } from 'Pages/DsRepricing/@components/tones'

/**
 * Schedule → Executions.
 *
 * One row per pass of the sweep, newest first. `repricing_sweep_run`
 * had been recording this since the sweep was built and nothing read
 * it: "how is it going" was a question only someone with a ClickHouse
 * client could answer.
 *
 * Three numbers per pass, from three places, answering different
 * questions:
 *   composed / created  — did the configuration produce searches?
 *   searches finished   — are the scrapers done?
 *   decisions           — did the engine act, and how?
 *
 * The middle one is allowed to be missing. Until
 * `NEW_SEARCH.id_repricing_execution` is indexed the count takes
 * minutes, so the server abandons it and says so; this draws "not
 * available" rather than a bar at zero, which would read as "nothing
 * has finished".
 */


// "19 Oct 2026 09:21", in the browser's time as before: the day as every table of the app writes it
const fmtSlot = (iso) => {
	if (!iso) return '—'
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return String(iso)
	return `${displayDay(format(d, 'yyyy-MM-dd'))} ${format(d, 'HH:mm')}`
}

export default function ExecutionsTab() {
	const [state, setState] = useState({ loading: true, items: [], progressOk: true })
	const [open, setOpen] = useState(null)
	const [changes, setChanges] = useState(null)   // { run, type } behind the popup

	const load = useCallback(async () => {
		setState(s => ({ ...s, loading: true }))
		try {
			const body = await scheduleApi.executions(30)
			setState({ loading: false, items: body?.items || [],
			           progressOk: body?.progress_ok !== false, error: null })
		} catch (e) {
			setState({ loading: false, items: [], progressOk: true,
			           error: e?.message || 'Could not load the executions' })
		}
	}, [])

	useEffect(() => { load() }, [load])

	return (
		<Box>
			{/* The card's head as the app's tables: icon title, the line about it, its action */}
			<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					{/* What the card is, behind the ⓘ of its title */}
					<AppCardTitle icon="list-check" title="Executions">
						<AppInfoButton title="Executions" sx={{ ml: 0.25 }}>
						Every pass of the sweep, newest first. A pass launches the searches
						one schedule rule asks for; the engine decides once each search
						comes back.
						</AppInfoButton>
					</AppCardTitle>
				</Box>
				<Button variant="outlined" startIcon={<RefreshIcon sx={{ fontSize: 14 }}/>}
				        onClick={load} disabled={state.loading}
				        sx={secondaryButtonSx}>
					Refresh
				</Button>
			</Box>

			{state.error && (
				<AppNote severity="error" sx={{ mb: 2 }}>
					{state.error}
				</AppNote>
			)}

			{!state.progressOk && !state.error && (
				<AppNote sx={{ mb: 2 }}>
					Search progress is not available yet: counting them needs an index
					on <code>NEW_SEARCH</code> that has not been created. Everything
					else on this screen is live.
				</AppNote>
			)}

			{state.loading && !state.items.length ? <AppTableSkeleton rows={6} columns={8}/> : (
				<Table size="small" sx={{
					'& tbody tr:nth-of-type(odd)':  { bgcolor: dsColorNew.white },
					'& tbody tr:nth-of-type(even)': { bgcolor: dsColorNew.tableRowAlt },
					'& td': { fontSize: 13, py: 1 },
				}}>
					<TableHead>
						<TableRow>
							<TableCell sx={{ width: 34 }}/>
							<TableCell>When</TableCell>
							<TableCell>Rule</TableCell>
							<TableCell align="center">Why</TableCell>
							<TableCell align="right">Searches</TableCell>
							<TableCell sx={{ minWidth: 180 }}>Finished</TableCell>
							<TableCell>Decisions</TableCell>
							<TableCell sx={{ minWidth: 150 }}>PMS push</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{!state.loading && state.items.length === 0 && !state.error && (
							<TableRow><TableCell colSpan={8} align="center" sx={{ py: 2 }}>
								<AppEmptyLine>
									No pass has run yet. The scheduler is paused; a rule can
									also be launched by hand from <strong>Schedule rules</strong>.
								</AppEmptyLine>
							</TableCell></TableRow>
						)}
						{state.items.map(run => (
							<React.Fragment key={run.id}>
								<Row run={run} progressOk={state.progressOk}
								     open={open === run.id}
								     onToggle={() => setOpen(open === run.id ? null : run.id)}
								     onOpenChanges={(type) => setChanges({ run, type })}/>
								<TableRow>
									<TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
										<Collapse in={open === run.id} unmountOnExit>
											<Detail run={run} onPushed={load}/>
										</Collapse>
									</TableCell>
								</TableRow>
							</React.Fragment>
						))}
					</TableBody>
				</Table>
			)}
			{changes && (
				<ChangesDialog type={changes.type}
				               title={`${changes.run.rule_name} · ${fmtSlot(changes.run.slot)}`}
				               query={{ execution_id: changes.run.execution_id }}
				               activityHref={`${DS_REPRICING_ROUTES.ACTIVITY_PATH}?execution_id=${encodeURIComponent(changes.run.execution_id)}&event_type=${encodeURIComponent(changes.type)}`}
				               activityLabel="Open in Activity, filtered on this pass"
				               emptyText="Nothing of this kind in this pass."
				               onClose={() => setChanges(null)}/>
			)}
		</Box>
	)
}



function Row({ run, progressOk, open, onToggle, onOpenChanges }) {
	const decisions = run.decisions || {}
	const total = Object.values(decisions).reduce((a, b) => a + b, 0)
	return (
		<TableRow sx={{ '&:hover': { bgcolor: dsColorNew.hoverBgLight } }}>
			<TableCell>
				<IconButton size="small" onClick={onToggle}>
					{open ? <CollapseIcon fontSize="small"/> : <ExpandIcon fontSize="small"/>}
				</IconButton>
			</TableCell>
			<TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtSlot(run.slot)}</TableCell>
			<TableCell sx={{ fontWeight: 600 }}>{run.rule_name}</TableCell>
			<TableCell align="center">
				<Chip size="small" label={run.reason === 'force' ? 'by hand' : 'scheduled'}
				      sx={toneChipSx(run.reason === 'force' ? 'info' : 'neutral')}/>
			</TableCell>
			<TableCell align="right" sx={{ fontFamily: 'inherit' }}>
				{run.created}
				{run.created !== run.composed && (
					<Tooltip title={`${run.composed} composed, ${run.created} created`}>
						<Box component="span" sx={{ color: dsColorNew.noteAmber, ml: 0.5 }}>
							/{run.composed}
						</Box>
					</Tooltip>
				)}
			</TableCell>
			<TableCell><Progress run={run} progressOk={progressOk}/></TableCell>
			<TableCell>
				{total === 0 ? (
					<Typography sx={{ fontSize: 12, color: dsColorNew.textSubtle,
					                  fontStyle: 'italic' }}>
						nothing yet
					</Typography>
				) : (
					<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
						{Object.entries(decisions)
							.sort((a, b) => b[1] - a[1])
							.map(([type, n]) => {
								const s = OUTCOME_STYLE[type]
									|| { tone: 'neutral', label: type }
								return (
									<Chip key={type} size="small" label={`${s.label} ${n}`}
									      onClick={() => onOpenChanges(type)}
									      title={`What this pass ${s.label}: one row per decision`}
									      sx={toneChipSx(s.tone, { clickable: true })}/>
								)
							})}
					</Box>
				)}
			</TableCell>
			<TableCell><PushCell push={run.push}/></TableCell>
		</TableRow>
	)
}

//: Did the ladders reach the PMS. A decision waits for its search to
//: close before it is sent (CU-869fb9wz6), so a pass has a fourth
//: question after composed / finished / decided: sent.
const PUSH_STYLE = {
	flushed:             { tone: 'ok',   label: 'sent' },
	flushed_by_deadline: { tone: 'warn', label: 'sent on deadline' },
	pending:             { tone: 'info', label: 'waiting' },
	flushing:            { tone: 'info', label: 'sending' },
	failed:              { tone: 'err',  label: 'failed' },
	// A newer search for the same office and date went out first:
	// these decisions are stale and will never be sent.
	superseded:          { tone: 'neutral', label: 'superseded' },
}

function PushCell({ push }) {
	if (!push || !push.searches) {
		return (
			<Typography sx={{ fontSize: 12, color: dsColorNew.textSubtle, fontStyle: 'italic' }}>
				no searches waiting
			</Typography>
		)
	}
	return (
		<Box>
			<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
				{Object.entries(PUSH_STYLE)
					.filter(([state]) => push[state])
					.map(([state, s]) => (
						<Chip key={state} size="small" label={`${s.label} ${push[state]}`}
						      sx={toneChipSx(s.tone)}/>
					))}
			</Box>
			<Typography sx={{ fontSize: 11, color: dsColorNew.textMuted, mt: 0.4 }}
			            title="Tasks that reached the engine, of the ones the launch path created">
				{push.tasks_arrived} of {push.tasks_expected} tasks
				{push.calls_ok ? ` · ${push.calls_ok} calls ok` : ''}
				{push.calls_error ? ` · ${push.calls_error} failed` : ''}
				{push.flush_count_max > 1 ? ` · sent ${push.flush_count_max}×` : ''}
			</Typography>
		</Box>
	)
}

function Progress({ run, progressOk }) {
	if (!progressOk || run.searches == null) {
		return (
			<Typography sx={{ fontSize: 12, color: dsColorNew.textSubtle,
			                  fontStyle: 'italic' }}>
				not available
			</Typography>
		)
	}
	const counts = run.searches || {}
	const done = (counts.finished || 0) + (counts.error || 0) + (counts['not-avail'] || 0)
	const total = Object.values(counts).reduce((a, b) => a + b, 0) || run.created || 0
	const pct = total ? Math.round((done / total) * 100) : 0
	return (
		<Box>
			<LinearProgress variant="determinate" value={pct}
			                sx={{ height: 6, borderRadius: 3, mb: 0.4 }}/>
			<Typography sx={{ fontSize: 11, color: dsColorNew.textMuted }}>
				{done} of {total}
				{counts.running ? ` · ${counts.running} running` : ''}
				{counts.error ? ` · ${counts.error} failed` : ''}
			</Typography>
		</Box>
	)
}

function Detail({ run, onPushed }) {
	const skipped = Object.entries(run.skipped || {}).filter(([, n]) => n)
	const push = run.push || {}
	// Waiting or given up — or already sent with calls the PMS refused,
	// which Push now resends (ladders refused only).
	const canPush = (push.pending || 0) + (push.failed || 0) + (push.calls_error || 0) > 0
	const { showToast } = useToast()
	const [pushing, setPushing] = useState(false)
	const pushNow = async () => {
		setPushing(true)
		try {
			const r = await scheduleApi.flushExecution(run.execution_id, 'Push now from Executions')
			const sent = (r.sent || []).length, refused = (r.refused || []).length
			showToast.success(`Sent ${sent} search${sent === 1 ? '' : 'es'}${refused ? `, ${refused} refused` : ''}.`)
			onPushed?.()
		} catch (e) {
			showToast.error(e?.code === 'nothing_pending' ? 'Nothing is waiting on this pass.'
			              : e?.code === 'all_refused' ? 'The engine refused every search: somebody is already sending.'
			              : (e?.message || 'Push now failed'))
		} finally {
			setPushing(false)
		}
	}
	return (
		<Box sx={{ bgcolor: dsColorNew.tableRowAlt, px: 3, py: 2,
		           borderTop: '1px solid', borderColor: dsColorNew.grayBorder }}>
			{canPush && (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
					<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, flex: 1 }}>
						{(push.pending || 0) + (push.failed || 0) > 0
							? `${push.pending || 0} search${(push.pending || 0) === 1 ? '' : 'es'} still waiting for the rest of their tasks${push.failed ? `, ${push.failed} given up` : ''}. Push now sends what has arrived; the brackets nobody decided keep the price the pricelist holds.`
							: `Every search was sent, but the PMS refused ${push.calls_error} call${push.calls_error === 1 ? '' : 's'}. Push now resends those ladders only.`}
						{(push.pending || 0) + (push.failed || 0) > 0 && push.calls_error ? ` ${push.calls_error} refused call${push.calls_error === 1 ? '' : 's'} are resent too.` : ''}
						{push.superseded ? ` ${push.superseded} other${push.superseded === 1 ? '' : 's'} went stale — a newer search for the same office and date was sent — and will not go.` : ''}
					</Typography>
					<AppBulkEditAction onClick={pushNow} disabled={pushing}>
						{pushing ? 'Sending…' : 'Push now'}
					</AppBulkEditAction>
				</Box>
			)}
			<Field label="Execution id" value={run.execution_id}/>
			<Field label="Started" value={fmtSlot(run.started_at)}/>
			<Field label="Finished" value={fmtSlot(run.finished_at)}/>
			{run.error_message && (
				<AppNote severity="error" sx={{ mt: 1.5 }}>
					{run.error_message}
				</AppNote>
			)}
			{/* Already recorded by the sweep and never shown until now: a
			    pass that composed fewer searches than expected says why
			    here, one reason at a time. */}
			{skipped.length > 0 && (
				<Box sx={{ mt: 1.5 }}>
					<Typography sx={{ fontSize: 12, fontWeight: 700,
					                  textTransform: 'uppercase', letterSpacing: '0.05em',
					                  color: dsColorNew.textMuted, mb: 0.75 }}>
						Not searched
					</Typography>
					{skipped.map(([reason, n]) => (
						<Box key={reason} sx={{ display: 'flex', gap: 1, fontSize: 13 }}>
							<Box sx={{ fontFamily: 'inherit', fontWeight: 700,
							           minWidth: 40, textAlign: 'right' }}>{n}</Box>
							<Box sx={{ color: dsColorNew.textMuted }}>
								{reason.replace(/_/g, ' ')}
							</Box>
						</Box>
					))}
				</Box>
			)}
		</Box>
	)
}

function Field({ label, value }) {
	if (!value) return null
	return (
		<Box sx={{ display: 'flex', gap: 1, fontSize: 13, mb: 0.3 }}>
			<Box sx={{ minWidth: 110, color: dsColorNew.textMuted }}>{label}</Box>
			<Box sx={{ fontFamily: 'inherit' }}>{value}</Box>
		</Box>
	)
}
